import React, { useState, useEffect } from 'react';
import { X, FileText, User, Calendar } from 'lucide-react';
import Button from '../../../components/ui/Button';
import documentRenewalBatchService from '../../../services/documentRenewalBatchService';
import WorkflowProgressPanel from './WorkflowProgressPanel';

const BatchDetailsModal = ({ batch, onClose, onRefresh }) => {
  const [batchItems, setBatchItems] = useState([]);
  const [workflowExecutions, setWorkflowExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('items');

  useEffect(() => {
    loadBatchDetails();
  }, [batch?.id]);

  const loadBatchDetails = async () => {
    try {
      setLoading(true);

      const [itemsData, workflowData] = await Promise.all([
        documentRenewalBatchService?.getBatchItems(batch?.id),
        documentRenewalBatchService?.getWorkflowExecutions(batch?.id)
      ]);

      setBatchItems(itemsData || []);
      setWorkflowExecutions(workflowData || []);
    } catch (err) {
      console.error('Error loading batch details:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    };
    return badges?.[status] || badges?.pending;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{batch?.batchName}</h2>
            <p className="text-sm text-gray-600 mt-1">{batch?.batchDescription}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Batch Summary */}
        <div className="p-6 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <p className="text-lg font-semibold text-gray-900 capitalize">
                {batch?.batchStatus?.replace('_', ' ')}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Priority</p>
              <p className="text-lg font-semibold text-gray-900 capitalize">
                {batch?.batchPriority}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Documents</p>
              <p className="text-lg font-semibold text-gray-900">
                {batch?.totalDocuments || 0}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Progress</p>
              <p className="text-lg font-semibold text-gray-900">
                {Math.round(batch?.workflowProgressPercentage || 0)}%
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('items')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'items' ?'border-emerald-500 text-emerald-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Documents ({batchItems?.length || 0})
            </button>
            <button
              onClick={() => setActiveTab('workflow')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'workflow' ?'border-emerald-500 text-emerald-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Workflow Progress
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : (
            <>
              {activeTab === 'items' && (
                <div className="space-y-4">
                  {batchItems?.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">No documents in this batch</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Employee
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Document
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Expiry Date
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {batchItems?.map((item) => (
                            <tr key={item?.id}>
                              <td className="px-4 py-3">
                                <div className="flex items-center">
                                  <User className="h-4 w-4 text-gray-400 mr-2" />
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">
                                      {item?.employeeName}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {item?.department}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-sm text-gray-900">{item?.documentName}</div>
                                <div className="text-xs text-gray-500 capitalize">
                                  {item?.documentType?.replace('_', ' ')}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center">
                                  <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                                  <div>
                                    <div className="text-sm text-gray-900">
                                      {formatDate(item?.currentExpiryDate)}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {item?.daysUntilExpiry} days
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(item?.itemStatus)}`}>
                                  {item?.itemStatus?.replace('_', ' ')?.toUpperCase()}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'workflow' && (
                <WorkflowProgressPanel
                  batchId={batch?.id}
                  workflowExecutions={workflowExecutions}
                  onRefresh={loadBatchDetails}
                />
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BatchDetailsModal;